import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { usePaymentStatus } from '@/core/domain/payment-gateway/transactions/transactions.hooks';
import {
  PaymentFinancialPendingStatus,
  TransactionEntityType,
} from '@/core/domain/payment-gateway/transactions/transactions.types';
import { Button } from 'antd';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { TEFProofOfPayment } from '../TEFProofOfPayment';
import { useDirectSalePaymentReceipt } from '@/core/domain/reports/financial/directSale/DirectSale.hook';

interface Props {
  refresh(status: PaymentFinancialPendingStatus): Promise<void> | void;
  entityType: TransactionEntityType;
  entityId: number | undefined;
  enabled: boolean;
  isAccountReceivable?: boolean;
}

export interface TEFProcessFinancialRef {
  statusRefetch: () => Promise<unknown>;
}

const TEFProcessFinancial = forwardRef<TEFProcessFinancialRef, Props>(
  function TEFProcessFinancial(props, ref) {
    const paymentStatus = usePaymentStatus(
      { entityId: props.entityId, entityType: props.entityType },
      true,
      (status) => {
        if (props.enabled) {
          props.refresh(status);
        }
      },
    );

    const directSaleReceipt = useDirectSalePaymentReceipt();

    useImperativeHandle(ref, () => ({
      statusRefetch: () => paymentStatus.refetch(),
    }));

    const status = useMemo(
      () => paymentStatus.data?.status,
      [paymentStatus.data?.status],
    );

    const isPaymentFinancialPending = useMemo(() => {
      const status = paymentStatus.data?.status;

      return (
        status === PaymentFinancialPendingStatus.PENDING &&
        paymentStatus.isSuccess
      );
    }, [status, paymentStatus.dataUpdatedAt]);

    const isPaymentFinancialProcessing = useMemo(() => {
      const status = paymentStatus.data?.status;

      return (
        (status === PaymentFinancialPendingStatus.PROCESSING ||
          status === PaymentFinancialPendingStatus.CANCELLING) &&
        paymentStatus.isSuccess
      );
    }, [status, paymentStatus.dataUpdatedAt]);

    const isPaymentFinancialFaild = useMemo(() => {
      const status = paymentStatus.data?.status;

      return (
        (status === PaymentFinancialPendingStatus.FAILED ||
          status === PaymentFinancialPendingStatus.CANCEL_FAILED) &&
        paymentStatus.isSuccess
      );
    }, [status, paymentStatus.dataUpdatedAt]);

    const isPaymentSaleCompleted = useMemo(() => {
      return (
        paymentStatus.data?.status ===
          PaymentFinancialPendingStatus.COMPLETED &&
        props.entityType === TransactionEntityType.Sale
      );
    }, [paymentStatus.data?.status, props.entityType]);

    if (isPaymentFinancialPending) {
      return (
        <ModalLoadingOverlay
          posAbsolute
          messages={[
            <>
              <p style={{ margin: 0 }}>
                Já existe uma transação pendente para esta{' '}
                {props.isAccountReceivable
                  ? 'conta a receber'
                  : props.entityType === TransactionEntityType.Sale
                  ? 'venda direta'
                  : 'orçamento'}
                !
              </p>
              <Button
                loading={paymentStatus.isFetching}
                onClick={() => paymentStatus.refetch()}
              >
                Revalidar
              </Button>
            </>,
          ]}
        />
      );
    }

    if (isPaymentFinancialProcessing && props.enabled) {
      return (
        <ModalLoadingOverlay
          posAbsolute
          messages={[
            <>
              <p style={{ margin: 0 }}>
                Já existe um{' '}
                {status === PaymentFinancialPendingStatus.CANCELLING
                  ? 'cancelamento'
                  : 'financeiro'}{' '}
                em processamento para{' '}
                {props.isAccountReceivable
                  ? 'esta conta a receber'
                  : props.entityType === TransactionEntityType.Sale
                  ? 'esta venda direta'
                  : 'este orçamento'}
                !
              </p>
              <Button
                loading={paymentStatus.isFetching}
                onClick={() => paymentStatus.refetch()}
              >
                Revalidar
              </Button>
            </>,
          ]}
        />
      );
    }

    if (isPaymentFinancialFaild) {
      return (
        <ModalLoadingOverlay
          posAbsolute
          disableLoading={!paymentStatus.isFetching}
          messages={[
            <>
              <p style={{ margin: 0 }}>
                houve um erro no processamento do{' '}
                {status === PaymentFinancialPendingStatus.CANCEL_FAILED
                  ? 'cancelamento do financeiro'
                  : 'gerar o financeiro'}{' '}
                para{' '}
                {props.isAccountReceivable
                  ? 'esta conta a receber'
                  : props.entityType === TransactionEntityType.Sale
                  ? 'esta venda direta'
                  : 'este orçamento'}
                !
                <br />
                Por favor, entre em contato com o nosso time de suporte para
                sanar o problema e liberar o financeiro.
              </p>
              <Button
                loading={paymentStatus.isFetching}
                onClick={() => paymentStatus.refetch()}
              >
                Revalidar
              </Button>
            </>,
          ]}
        />
      );
    }

    if (isPaymentSaleCompleted && !props.isAccountReceivable) {
      return (
        <TEFProofOfPayment
          showCustomerDocument
          entityId={props.entityId}
          generateReceipt={(entity) =>
            directSaleReceipt.mutateAsync({
              format: 'PDF',
              saleId: entity.entityId,
              receiptPrintTarget: entity.printTarget,
              receiptPrintType: entity.printType,
              showCustomerDocument: entity.showCustomerDocument,
            })
          }
        />
      );
    }

    return null;
  },
);

export default TEFProcessFinancial;
